import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Theme } from '@mui/material/styles';
import CheckroomRoundedIcon from '@mui/icons-material/CheckroomRounded';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { DashboardSectionHeader } from '@/shared/components/dashboard/DashboardSectionHeader';
import { dashboardPanelSx } from '@/shared/styles/surfaces';
import { INDIA_STATES } from '@/features/onboarding/constants';
import { apiErrorMessage } from '../api/growth.api';
import {
  useCancelMerchRequest,
  useConfirmMerchReceived,
  useCreateMerchRequest,
  useMerchOptions,
  useMerchRequests,
} from '../hooks/useGrowth';
import type { MerchRequest, MerchStatus } from '../types';
import { TRIPPY_SRC } from './PromoPostCanvas';
import { RewardChip } from './RewardChip';
import { useRewardsToast } from './RewardsToast';

const STEPS: MerchStatus[] = ['REQUESTED', 'APPROVED', 'SHIPPED', 'DELIVERED', 'RECEIVED'];
const STEP_INDEX: Partial<Record<MerchStatus, number>> = {
  REQUESTED: 0,
  OUT_OF_STOCK: 0,
  APPROVED: 1,
  PROCESSING: 1,
  SHIPPED: 2,
  DELIVERED: 3,
  RECEIVED: 4,
};

const schema = z.object({
  size: z.string().min(1, 'merch.dialog.errors.size'),
  line1: z.string().trim().min(3, 'merch.dialog.errors.line1'),
  line2: z.string().trim().optional(),
  city: z.string().trim().min(2, 'merch.dialog.errors.city'),
  state: z.string().trim().min(2, 'merch.dialog.errors.state'),
  pincode: z.string().trim().regex(/^\d{6}$/, 'merch.dialog.errors.pincode'),
  contactPhone: z.string().trim().regex(/^[6-9]\d{9}$/, 'merch.dialog.errors.phone'),
  instructions: z.string().trim().max(500).optional(),
});
type FormData = z.infer<typeof schema>;
type ErrorKey =
  | 'merch.dialog.errors.size'
  | 'merch.dialog.errors.line1'
  | 'merch.dialog.errors.city'
  | 'merch.dialog.errors.state'
  | 'merch.dialog.errors.pincode'
  | 'merch.dialog.errors.phone';

function MerchRequestDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation('rewards');
  const notify = useRewardsToast();
  const fullScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const options = useMerchOptions();
  const create = useCreateMerchRequest();
  const [serverError, setServerError] = useState('');
  const err = (message?: string) => (message ? t(message as ErrorKey) : undefined);

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { size: '', line1: '', line2: '', city: '', state: '', pincode: '', contactPhone: '', instructions: '' },
  });

  useEffect(() => {
    if (!open) {
      reset();
      setServerError('');
    }
  }, [open, reset]);

  const submit = (data: FormData) => {
    setServerError('');
    create.mutate(
      {
        size: data.size,
        address: { line1: data.line1, line2: data.line2 || undefined, city: data.city, state: data.state, pincode: data.pincode },
        contactPhone: data.contactPhone,
        instructions: data.instructions || undefined,
      },
      {
        onSuccess: () => {
          notify(t('merch.dialog.success'));
          onClose();
        },
        onError: (error) => setServerError(apiErrorMessage(error, t('common.error'))),
      }
    );
  };

  return (
    <Dialog open={open} onClose={create.isPending ? undefined : onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <Box component="form" onSubmit={handleSubmit(submit)} noValidate>
        <DialogTitle sx={{ fontWeight: 800 }}>{t('merch.dialog.title')}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2 }}>
          <Box>
            <Typography fontWeight={700} sx={{ mb: 1 }}>
              {t('merch.dialog.size')}
            </Typography>
            {options.isLoading ? (
              <Skeleton variant="rounded" height={40} />
            ) : (
              <Controller
                name="size"
                control={control}
                render={({ field }) => (
                  <ToggleButtonGroup
                    exclusive
                    value={field.value}
                    onChange={(_e, value: string | null) => value && field.onChange(value)}
                    aria-label={t('merch.dialog.size')}
                    sx={{ flexWrap: 'wrap', gap: 1, '& .MuiToggleButton-root': { border: '1px solid', borderColor: 'divider', borderRadius: '10px !important', minWidth: 56, fontWeight: 700 } }}
                  >
                    {(options.data?.sizes ?? []).map((size) => (
                      <ToggleButton key={size.size} value={size.size} title={size.inStock ? undefined : t('merch.dialog.outOfStock')}>
                        {size.size}
                        {!size.inStock && <Box component="span" sx={{ ml: 0.5, fontSize: '0.6rem', color: 'warning.dark' }}>•</Box>}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}
              />
            )}
            {errors.size && <Typography variant="caption" color="error">{err(errors.size.message)}</Typography>}
            {options.data?.sizeChartNote && (
              <Alert severity="info" icon={false} sx={{ mt: 1.5 }}>
                <Typography variant="caption" fontWeight={700} display="block">{t('merch.dialog.sizeChart')}</Typography>
                <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>{options.data.sizeChartNote}</Typography>
              </Alert>
            )}
          </Box>

          <Controller name="line1" control={control} render={({ field }) => (
            <TextField {...field} label={t('merch.dialog.line1')} required fullWidth autoComplete="address-line1" error={!!errors.line1} helperText={err(errors.line1?.message)} />
          )} />
          <Controller name="line2" control={control} render={({ field }) => (
            <TextField {...field} label={t('merch.dialog.line2')} fullWidth autoComplete="address-line2" />
          )} />
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <Controller name="city" control={control} render={({ field }) => (
              <TextField {...field} label={t('merch.dialog.city')} required autoComplete="address-level2" error={!!errors.city} helperText={err(errors.city?.message)} />
            )} />
            <Controller name="state" control={control} render={({ field }) => (
              <TextField {...field} select label={t('merch.dialog.state')} required error={!!errors.state} helperText={err(errors.state?.message)}>
                {INDIA_STATES.map((state) => <MenuItem key={state} value={state}>{state}</MenuItem>)}
              </TextField>
            )} />
            <Controller name="pincode" control={control} render={({ field }) => (
              <TextField {...field} label={t('merch.dialog.pincode')} required inputMode="numeric" autoComplete="postal-code" inputProps={{ maxLength: 6 }} error={!!errors.pincode} helperText={err(errors.pincode?.message)} />
            )} />
            <Controller name="contactPhone" control={control} render={({ field }) => (
              <TextField {...field} label={t('merch.dialog.phone')} required inputMode="tel" autoComplete="tel-national" inputProps={{ maxLength: 10 }} error={!!errors.contactPhone} helperText={err(errors.contactPhone?.message)} />
            )} />
          </Box>
          <Controller name="instructions" control={control} render={({ field }) => (
            <TextField {...field} label={t('merch.dialog.instructions')} fullWidth multiline minRows={2} />
          )} />
          {serverError && <Alert severity="error">{serverError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} disabled={create.isPending}>{t('merch.dialog.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={create.isPending} sx={{ borderRadius: 999, fontWeight: 700 }}>
            {create.isPending ? t('merch.dialog.submitting') : t('merch.dialog.submit')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function CurrentRequest({ request, onUploadSelfie }: { request: MerchRequest; onUploadSelfie: () => void }) {
  const { t } = useTranslation('rewards');
  const notify = useRewardsToast();
  const cancel = useCancelMerchRequest();
  const received = useConfirmMerchReceived();
  const closed = ['REJECTED', 'CANCELLED'].includes(request.status);
  const active = STEP_INDEX[request.status] ?? 0;

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography fontWeight={800}>{t('merch.current')}</Typography>
        <RewardChip label={t(`status.merch.${request.status}`)} status={request.status} />
        <Typography variant="body2" color="text.secondary">
          {t('merch.size', { size: request.size })} · {dayjs(request.createdAt).format('D MMM YYYY')}
        </Typography>
      </Box>
      {!closed && (
        <Stepper activeStep={request.status === 'RECEIVED' ? STEPS.length : active} alternativeLabel sx={{ '& .MuiStepLabel-label': { fontSize: '0.75rem' } }}>
          {STEPS.map((step) => (
            <Step key={step}>
              <StepLabel>{t(`merch.steps.${step as 'REQUESTED'}`)}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}
      {request.dispatch?.trackingId && (
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShippingOutlinedIcon fontSize="small" color="action" />
          {t('merch.tracking', { courier: request.dispatch.courier ?? '', trackingId: request.dispatch.trackingId })}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {['SHIPPED', 'DELIVERED'].includes(request.status) && (
          <Button
            variant="contained"
            disabled={received.isPending}
            onClick={() => received.mutate(request._id, { onError: (e) => notify(apiErrorMessage(e, t('common.error')), 'error') })}
            sx={{ borderRadius: 999, fontWeight: 700, bgcolor: '#1A2E1A', '&:hover': { bgcolor: '#243D24' } }}
          >
            {t('merch.confirmReceived')}
          </Button>
        )}
        {['DELIVERED', 'RECEIVED'].includes(request.status) && (
          <Button variant="outlined" onClick={onUploadSelfie} sx={{ borderRadius: 999, fontWeight: 700 }}>
            {t('merch.uploadSelfie')}
          </Button>
        )}
        {['REQUESTED', 'OUT_OF_STOCK'].includes(request.status) && (
          <Button
            color="inherit"
            disabled={cancel.isPending}
            onClick={() => {
              if (window.confirm(t('merch.cancelConfirm'))) {
                cancel.mutate(request._id, { onError: (e) => notify(apiErrorMessage(e, t('common.error')), 'error') });
              }
            }}
          >
            {t('merch.cancel')}
          </Button>
        )}
      </Box>
    </Box>
  );
}

interface MerchandiseSectionProps {
  requestOpen: boolean;
  onRequestOpenChange: (open: boolean) => void;
  onUploadSelfie: () => void;
}

export function MerchandiseSection({ requestOpen, onRequestOpenChange, onUploadSelfie }: MerchandiseSectionProps) {
  const { t } = useTranslation('rewards');
  const options = useMerchOptions();
  const requests = useMerchRequests();
  const open = requests.data?.find((r) => !['REJECTED', 'CANCELLED'].includes(r.status));
  const latest = open ?? requests.data?.[0];
  const canRequest = options.data?.available && !open;

  return (
    <Box id="merch" sx={{ scrollMarginTop: 88 }}>
      <DashboardSectionHeader title={t('merch.title')} subtitle={t('merch.subtitle')} />
      <Paper elevation={0} sx={{ ...dashboardPanelSx, p: { xs: 2.5, md: 3 }, display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '200px 1fr' }, alignItems: 'center' }}>
        <Box
          sx={{
            display: { xs: 'none', md: 'grid' },
            placeItems: 'center',
            height: 200,
            borderRadius: 3,
            background: 'radial-gradient(circle at 50% 45%, #F5EBD3 0%, #E9D7AE 50%, #C8A96E 100%)',
            overflow: 'hidden',
          }}
        >
          <Box component="img" src={TRIPPY_SRC} alt="" sx={{ height: 190, mt: 2 }} />
        </Box>
        <Box>
          {requests.isLoading || options.isLoading ? (
            <Skeleton variant="rounded" height={120} />
          ) : requests.isError ? (
            <Alert severity="error">{t('common.error')}</Alert>
          ) : latest ? (
            <CurrentRequest request={latest} onUploadSelfie={onUploadSelfie} />
          ) : (
            <Box sx={{ display: 'grid', gap: 1.5, justifyItems: 'start' }}>
              <Typography fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckroomRoundedIcon /> {options.data?.name ?? t('merch.itemName')}
              </Typography>
              {!options.data?.available && <Typography color="text.secondary">{t('merch.notOpen')}</Typography>}
            </Box>
          )}
          {canRequest && (
            <Button
              variant="contained"
              onClick={() => onRequestOpenChange(true)}
              startIcon={<CheckroomRoundedIcon />}
              sx={{ mt: 2, borderRadius: 999, fontWeight: 700, bgcolor: '#1A2E1A', '&:hover': { bgcolor: '#243D24' } }}
            >
              {t('merch.request')}
            </Button>
          )}
        </Box>
      </Paper>
      <MerchRequestDialog open={requestOpen} onClose={() => onRequestOpenChange(false)} />
    </Box>
  );
}
